'use client'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
} from '@/components/extension/responsive-modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAddressCreateMutation } from '@/hooks/react-query/account/address/use-address-create.mutation'
import { useUserQuery } from '@/hooks/react-query/user/use-user'
import { KolProfile } from '@/types/campaign'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { CampaignAddressFields } from './campaign-address-fields'
import {
  CampaignRegisterFormData,
  campaignRegisterDefaultValues,
  createCampaignRegisterSchema,
} from './campaign-register-schema'

interface CampaignShippingEditModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  kolProfile: KolProfile
  onSave: (profile: KolProfile) => void
}

export const CampaignShippingEditModal = ({
  open,
  onOpenChange,
  kolProfile,
  onSave,
}: CampaignShippingEditModalProps) => {
  const t = useTranslations('community_threads.campaign')
  const tFields = useTranslations('community_threads.campaign.fields')
  const { data: user } = useUserQuery()
  const { mutateAsync: createAddress } = useAddressCreateMutation()

  const form = useForm<CampaignRegisterFormData>({
    resolver: zodResolver(createCampaignRegisterSchema(t)),
    defaultValues: campaignRegisterDefaultValues,
  })
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form

  // 3 field city/district/ward đã có sẵn từ kolProfile -> khỏi geocode lại.
  const initialAddress = useMemo(
    () => ({
      city: kolProfile.city,
      district: kolProfile.district,
      ward: kolProfile.ward,
    }),
    [kolProfile]
  )

  useEffect(() => {
    if (!open) return
    // Chỉ cho sửa tên, SĐT và địa chỉ - các field còn lại (email, social,
    // sports...) giữ nguyên từ hồ sơ đã lưu, agreePolicy set sẵn true vì đã
    // đồng ý từ lúc tạo hồ sơ.
    reset({
      ...campaignRegisterDefaultValues,
      ...kolProfile,
      socialLinks: [...kolProfile.socialLinks],
      agreePolicy: true,
    })
  }, [open, reset, kolProfile])

  // Chỉ cập nhật thông tin của payload chuẩn bị gửi đăng ký campaign (không
  // gọi API lưu hồ sơ KOL) - nếu là địa chỉ mới chưa có trong sổ địa chỉ thì
  // lưu thêm vào sổ, giống luồng ở trang cart.
  const onSubmit = async (data: CampaignRegisterFormData) => {
    const isNewAddress =
      !!data.address &&
      !!data.city &&
      !!data.district &&
      !!data.ward &&
      !user?.addresses?.some(
        (addr) =>
          addr.address === data.address &&
          addr.city === data.city &&
          addr.district === data.district &&
          addr.ward === data.ward
      )

    if (isNewAddress) {
      try {
        await createAddress({
          body: {
            fullname: data.fullName,
            phone: data.phone,
            address: data.address,
            city: data.city,
            district: data.district,
            ward: data.ward,
          },
        })
      } catch {
        // Không chặn luồng đăng ký campaign nếu lưu vào sổ địa chỉ thất bại.
      }
    }

    onSave({
      ...kolProfile,
      fullName: data.fullName,
      phone: data.phone,
      address: data.address,
      city: data.city,
      district: data.district,
      ward: data.ward,
    })
    onOpenChange(false)
  }

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent
        className='gap-0 border-none px-5 py-6 lg:max-w-lg lg:p-8'
        aria-describedby='modal-campaign-shipping-edit'
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ResponsiveModalTitle />
        <ResponsiveModalDescription />

        <h3 className='mb-1 font-criteria text-2xl font-bold text-neutral-900'>
          {t('shipping_edit_modal.title')}
        </h3>
        <p className='mb-5 text-sm text-neutral-600'>
          {t('shipping_edit_modal.description')}
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <Input
            id='fullName'
            label={tFields('full_name_label')}
            placeholder={tFields('full_name_placeholder')}
            {...register('fullName')}
            error={errors.fullName}
            wrapperClassName='mt-1'
          />
          <Input
            id='phone'
            type='tel'
            label={tFields('phone_label')}
            placeholder={tFields('phone_placeholder')}
            {...register('phone')}
            error={errors.phone}
            wrapperClassName='mt-1'
          />

          <CampaignAddressFields form={form} initialAddress={initialAddress} />

          <Button
            type='submit'
            variant='black'
            className='w-full'
            loading={isSubmitting}
          >
            {t('shipping_edit_modal.submit')}
          </Button>
        </form>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
